// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — colour system + WCAG contrast helper (principle #22). The play surface is a dark
// board on which threads and beads are drawn. The colours that carry MEANING and must clear
// the 3:1 graphic floor against every surface they sit on are: the BEAD, the CLEAN thread
// (cyan), the CROSSED thread (coral), the crossing ✕ MARK, the GRABBED-bead accent, and the
// SOLVED glow (green). Colour is never the only cue — a bead is a filled disc, a clean thread
// is a thin line, a crossed thread is a THICK line with a ✕ where it cuts, and solving turns
// the whole web green. Every meaningful colour is pinned by contrast.test.ts.

export const PALETTE = {
  // board + panels
  bg: '#111826',
  bgDeep: '#0b111d',
  board: '#111826', // the play surface — threads/beads are drawn on this
  panel: '#1a2438',
  panelDeep: '#0d1420',
  edge: '#2b3a58',

  // text
  text: '#eef3fb',
  textMuted: '#a9b7d4',
  ink: '#201603', // dark ink drawn ON the bright bead

  // play-surface roles (each also has a shape/behaviour cue)
  bead: '#ffc24a', // a filled amber disc
  beadGrab: '#fff0c2', // the lifted bead, brighter
  clean: '#33d6e0', // a thin cyan thread with no crossing
  crossed: '#ff7a6b', // a thick coral thread that crosses another
  mark: '#ff9d6b', // the ✕ at a crossing point
  solved: '#7be0a0', // the whole web when it is untangled
} as const;

/* ── WCAG contrast ────────────────────────────────────────────────────────── */

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance of a #rrggbb colour (WCAG 2.1). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a hex colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h, 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb colours (1..21, symmetric). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
